import React from "react";
import SignatureCanvas from "react-signature-canvas";
import {
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  FormControl,
  FormHelperText,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Typography,
} from "@mui/material";

function TechnicianSignatureCard({
  accomplishedBySignatureRef,
  canvasWidth,
  ackHeight,
  technicianSignatory,
  setTechnicianSignatory,
  setValue,
  clearErrors,
  errors,
  jobOrder,
  clearAccomplishedSignature,
  saveAndUploadAccomplishedSignature,
  isLoading,
  isTechnician,
  technicianSignatureUploadedAt,
  register,
  openEditTechs,

  jobDate = "",
  startTime = "",
  showSchedule = true,
  readOnlySchedule = true,
  onJobDateChange,
  onStartTimeChange,
  allowEditTechnicians = true,
}) {
  return (
    <Grid item xs={12}>
      <Card variant="outlined" sx={{ mb: 2 }}>
        <CardHeader
          title={
            <Typography
              variant="subtitle2"
              sx={{ fontWeight: "bold", textTransform: "uppercase" }}
            >
              Service Accomplished By
            </Typography>
          }
          sx={{ px: 2, pt: 2, pb: 0 }}
        />

        <CardContent sx={{ px: 2, pb: 2 }}>
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <Typography variant="subtitle1" sx={{ mb: 1 }}>
                Service Accomplished by:
              </Typography>

              {allowEditTechnicians && (
                <Button
                  size="small"
                  variant="outlined"
                  onClick={openEditTechs}
                  disabled={isLoading}
                  sx={{ mb: 2 }}
                >
                  Edit Technicians
                </Button>
              )}
            </Grid>

            <Grid item xs={12}>
              <Grid container spacing={1}>
                {jobOrder?.technicians?.length > 0 ? (
                  jobOrder.technicians.map((technician, index) => (
                    <Grid item xs={12} key={technician.id} sx={{ mb: 1 }}>
                      <TextField
                        label={`Technician ${index + 1}`}
                        variant="outlined"
                        fullWidth
                        value={`${technician.firstname} ${technician.lastname}`}
                        disabled
                        size="small"
                      />
                    </Grid>
                  ))
                ) : (
                  <Grid item xs={12}>
                    <TextField
                      label="Technician"
                      variant="outlined"
                      fullWidth
                      value="No Technicians Available"
                      InputProps={{ readOnly: true }}
                      size="small"
                    />
                  </Grid>
                )}
              </Grid>
            </Grid>

            <Grid item xs={12} md={6}>
              <Box
                sx={{
                  width: canvasWidth,
                  height: ackHeight,
                  border: "2px solid #000",
                  p: 1,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  overflow: "hidden",
                }}
              >
                <SignatureCanvas
                  ref={accomplishedBySignatureRef}
                  penColor="black"
                  canvasProps={{
                    width: canvasWidth,
                    height: ackHeight,
                    className: "sigCanvas",
                  }}
                />
              </Box>
            </Grid>

            <Grid item xs={12} md={6}>
              <FormControl fullWidth error={!!errors?.technician_signatory}>
                <InputLabel id="technician-signatory-label">
                  Technician Signatory
                </InputLabel>
                <Select
                  labelId="technician-signatory-label"
                  id="accomplished-by-technician"
                  value={technicianSignatory}
                  label="Technician Signatory"
                  onChange={(e) => {
                    const val = e.target.value;
                    setTechnicianSignatory(val);
                    setValue("technician_signatory", val, {
                      shouldValidate: true,
                      shouldDirty: true,
                    });
                    clearErrors("technician_signatory");
                  }}
                >
                  {jobOrder?.technicians?.map((tech) => (
                    <MenuItem
                      key={tech.id}
                      value={`${tech.firstname} ${tech.lastname}`}
                    >
                      {`${tech.firstname} ${tech.lastname}`}
                    </MenuItem>
                  ))}
                </Select>

                {errors?.technician_signatory && (
                  <FormHelperText>
                    {errors.technician_signatory.message}
                  </FormHelperText>
                )}
              </FormControl>

              <input type="hidden" {...register("technician_signatory")} />

              <Box sx={{ mt: 2, display: "flex", gap: 1, flexWrap: "wrap" }}>
                <Button
                  variant="outlined"
                  color="secondary"
                  onClick={clearAccomplishedSignature}
                  disabled={isLoading}
                  sx={{
                    fontSize: "0.875rem",
                    color: "#FF4C4C",
                    borderColor: "#FF4C4C",
                  }}
                >
                  Clear
                </Button>

                <Button
                  variant="contained"
                  color="primary"
                  onClick={saveAndUploadAccomplishedSignature}
                  disabled={isLoading}
                  sx={{ fontSize: "0.875rem" }}
                >
                  Upload
                </Button>
              </Box>

              {(errors?.accomplishedUploaded || errors?.accomplishedSignature) && (
                <FormHelperText error sx={{ mt: 1 }}>
                  {errors.accomplishedUploaded?.message ||
                    errors.accomplishedSignature?.message}
                </FormHelperText>
              )}

              {!isTechnician && technicianSignatureUploadedAt && (
                <Box
                  sx={{
                    mt: 2,
                    p: 2,
                    bgcolor: "#e3f2fd",
                    borderRadius: 1,
                    border: "1px solid #2196f3",
                  }}
                >
                  <Typography
                    variant="body2"
                    sx={{ color: "#1565c0", fontWeight: 600 }}
                  >
                    Technician Signature Uploaded At:
                  </Typography>

                  <Typography
                    variant="body2"
                    sx={{ color: "#0d47a1", mt: 0.5 }}
                  >
                    {new Date(technicianSignatureUploadedAt).toLocaleString(
                      "en-US",
                      {
                        month: "short",
                        day: "2-digit",
                        year: "numeric",
                        hour: "2-digit",
                        minute: "2-digit",
                        second: "2-digit",
                        hour12: true,
                      }
                    )}
                  </Typography>

                  <Typography
                    variant="caption"
                    sx={{ color: "#1976d2", mt: 0.5, display: "block" }}
                  >
                    This timestamp is saved and cannot be changed.
                  </Typography>
                </Box>
              )}
            </Grid>

            {showSchedule && (
              <Grid item xs={12}>
                <Card variant="outlined">
                  <CardHeader
                    title={
                      <Typography
                        variant="h6"
                        sx={{
                          fontWeight: "bold",
                          textTransform: "uppercase",
                          fontSize: "1rem",
                          mb: 2
                        }}
                      >
                        Schedule
                      </Typography>
                    }
                  />
                  <CardContent sx={{ pt: 0 }}>
                    <Grid container spacing={3}>
                      <Grid item xs={12} md={4}>
                        <TextField
                          label="Start Date"
                          name="job_date"
                          type="date"
                          value={jobDate || ""}
                          onChange={(e) => onJobDateChange?.(e.target.value)}
                          fullWidth
                          size="small"
                          disabled={readOnlySchedule}
                          InputLabelProps={{ shrink: true }}
                        />
                      </Grid>

                      <Grid item xs={12} md={4}>
                        <TextField
                          label="Start Time"
                          name="start_time"
                          type="time"
                          value={startTime || ""}
                          onChange={(e) => onStartTimeChange?.(e.target.value)}
                          fullWidth
                          size="small"
                          disabled={readOnlySchedule}
                          InputLabelProps={{ shrink: true }}
                        />
                      </Grid>
                    </Grid>
                  </CardContent>
                </Card>
              </Grid>
            )}
          </Grid>
        </CardContent>
      </Card>
    </Grid>
  );
}

export default TechnicianSignatureCard;